import { type Lang } from "@/lib/i18n";
import { type SubPageContent } from "@/components/common/SubPageLayout";

const MULTILAYER_HERO_IMAGE =
  "https://mutualfilter.com/wp-content/uploads/2026/09/Multi-Layer-Sintered-Woven-Wire-Mesh-Discs.jpg";
const THREE_LAYER_IMAGE =
  "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sintered_five_layer_mesh_structure.png";
const MULTI_LAYER_IMAGE =
  "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-precision-s-cb8314.png";
const FIVE_LAYER_IMAGE =
  "https://mutualfilter.com/wp-content/uploads/2026/09/Sintered-Mesh-Filter-Elements-of-alloyfilter.jpg?v=hy0enjdkewc";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "SinteredMultilayerMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Sintered Wire Mesh",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Sintered Multilayer Wire Mesh",
      lead: "Solid-state diffusion-bonded under high vacuum and controlled thermal cycles, our sintered multilayer wire mesh laminates merge micron-grade pore stability, robust mechanical strength, and superior backwash cleanability.",
      secondaryCta: {
        label: "Sintered Wire Mesh Hub",
        to: "/SinteredWireMeshPage",
      },
      image: {
        src: MULTILAYER_HERO_IMAGE,
        alt: "Vacuum diffusion bonded sintered multilayer wire mesh laminate",
      },
      cardNote: "Laminated Mesh Structures",
      cardLink: {
        label: "Request Specifications",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Filtration Rating", value: "1 – 200 µm" },
        { label: "Standard Alloys", value: "AISI 316L / 304L" },
        { label: "Laminate Thickness", value: "0.5 – 10.0 mm" },
        { label: "Bonding Technology", value: "Solid-State Diffusion" },
      ],
    },
    overview: {
      eyebrow: "Structural Precision",
      heading: "Vacuum Diffusion-Bonded Laminate Architecture",
      paragraphs: [
        "Sintered multilayer wire mesh integrates multiple individual woven wire cloth layers into an inseparable porous plate. Under high vacuum and elevated temperatures, metallic contact points undergo atomic migration without melting or filler brazing, eliminating pore displacement and wire shedding.",
        "Each layer in the sintered composite performs an engineered role: exterior protective mesh prevents mechanical abrasions, interior precision cloth dictates micron cut-off accuracy, and heavier backing meshes contribute structural rigidity against alternating process flows.",
        "Because wire intersections are permanently bonded, the aperture matrix retains fixed dimensions during high differential pressure spikes, cyclic pulse cleaning, and ultrasonic regeneration.",
      ],
      keyPoints: [
        "Solid-state diffusion bonding locks all wire intersections without solder or binders.",
        "Permanent pore geometry withstands repeated backwashing and high differential pressures.",
        "Standard 3-layer, 5-layer, and bespoke multi-layer reinforced composites available.",
      ],
      image: {
        src: MULTI_LAYER_IMAGE,
        alt: "Macro photograph of multi-layer diffusion-bonded sintered wire mesh composite",
      },
    },
    specTable: {
      heading: "Composite Specification Parameters",
      intro: "Standard multilayer configurations available in AISI 316L and 304L stainless steel grades for precision industrial filtration and fluidization.",
      columns: [
        { key: "configuration", label: "Configuration", align: "left" },
        { key: "layers", label: "Layer Count", align: "right" },
        { key: "nominalRating", label: "Nominal Rating (µm)", align: "right" },
        { key: "nominalThickness", label: "Nominal Thickness (mm)", align: "right" },
        { key: "primaryStructure", label: "Primary Layer Composition", align: "left" },
      ],
      rows: [
        {
          configuration: "3-Layer Standard Laminate",
          layers: "3",
          nominalRating: "2 – 100",
          nominalThickness: "1.70",
          primaryStructure: "Protective + Precision Control + Drainage Support",
        },
        {
          configuration: "5-Layer Standard Sintered Plate",
          layers: "5",
          nominalRating: "1 – 200",
          nominalThickness: "1.70",
          primaryStructure: "Protection + Control + Dispersion + 2 Reinforcing Meshes",
        },
        {
          configuration: "Multi-Layer Heavy Reinforcement",
          layers: "2 – 9+",
          nominalRating: "1 – 200",
          nominalThickness: "0.5 – 10.0",
          primaryStructure: "Multi-cloth pack with optional perforated base support",
        },
      ],
      caption: "Values represent standard configurations. Custom layer combinations and sheet dimensions engineered on request.",
    },
    features: {
      heading: "Key Engineering Characteristics",
      intro: "Designed for demanding separation conditions requiring dimensional certainty and long-term mechanical resilience.",
      items: [
        {
          title: "Rigid Monolithic Geometry",
          text: "Thermal diffusion fuses wire intersections into a structural porous plate that eliminates wire migration and pore enlargement under continuous load.",
        },
        {
          title: "Broad Filtration Range",
          text: "Available from 1 µm to 200 µm nominal ratings in standardized or custom multi-layer weave combinations for liquid and gas separation.",
        },
        {
          title: "Superior Backwash Ability",
          text: "Fixed aperture pathways allow efficient reverse pulse cleaning, chemical CIP regeneration, and ultrasonic baths without media degradation.",
        },
        {
          title: "High Machinability",
          text: "Can be cut, rolled, pleated, and welded into cylindrical cartridges, seamless cones, circular discs, and flanged filter plates.",
        },
      ],
    },
    related: {
      heading: "Multilayer Construction Variants",
      intro: "Explore standardized and customized multilayer sintered configurations engineered for distinct differential pressure and permeability profiles.",
      cards: [
        {
          title: "3 Layer Sintered Mesh",
          text: "Combines a protective layer, fine control mesh, and heavy backing grid for balanced flow rate and dependable mechanical support.",
          to: "/ThreeLayerSinteredMeshPage",
          image: {
            src: THREE_LAYER_IMAGE,
            alt: "Cross section of 3-layer sintered stainless steel mesh laminate",
          },
          badge: "3-Layer",
          linkLabel: "View 3-Layer Specifications",
        },
        {
          title: "5 Layer Sintered Mesh",
          text: "The universal industrial configuration combining protection, control, dispersion, and dual skeleton layers for maximum stability.",
          to: "/LayerStandardSinteredMeshPage",
          image: {
            src: FIVE_LAYER_IMAGE,
            alt: "5-layer standard sintered wire mesh laminate structure",
          },
          badge: "5-Layer Standard",
          linkLabel: "View 5-Layer Specifications",
        },
        {
          title: "Multi Layer Sintered Mesh",
          text: "Engineered multi-layer composites and perforated plate backings built to withstand severe pressure fluctuations and abrasive streams.",
          to: "/MultiLayerPage",
          image: {
            src: MULTI_LAYER_IMAGE,
            alt: "Macro view of multi-layer sintered stainless steel wire mesh laminate",
          },
          badge: "Custom Composite",
          linkLabel: "View Multi-Layer Specifications",
        },
      ],
    },
    cta: {
      id: "sintered-multilayer-cta",
      eyebrow: "Custom Engineering",
      heading: "Specify Your Multilayer Sintered Structure",
      lead: "From standard 5-layer plates to heavy-duty custom composites, our team supplies finished sheets, discs, and custom-formed filter assemblies tailored to your exact process parameters.",
      secondaryLabel: "Contact Engineering",
      secondaryTo: "/ContactPage",
      note: "Standard sheets and precision fabricated components supplied in AISI 316L and 304L.",
    },
  },
  zh: {
    breadcrumbKey: "SinteredMultilayerMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "烧结丝网产品系列",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "多层烧结不锈钢丝网",
      lead: "在严格的高温高真空环境下通过固态扩散焊接技术成型，多层烧结金属丝网板融合了稳定的微米级孔径分布、卓越的机械承压刚性以及出色的反冲洗再生性能。",
      secondaryCta: {
        label: "返回烧结丝网总览",
        to: "/SinteredWireMeshPage",
      },
      image: {
        src: MULTILAYER_HERO_IMAGE,
        alt: "高温真空扩散焊接多层烧结不锈钢丝网复合板",
      },
      cardNote: "复合多孔结构板",
      cardLink: {
        label: "获取技术参数",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "过滤精度", value: "1 – 200 µm" },
        { label: "标准材质", value: "AISI 316L / 304L" },
        { label: "公称厚度", value: "0.5 – 10.0 mm" },
        { label: "结合工艺", value: "高温真空固态扩散焊" },
      ],
    },
    overview: {
      eyebrow: "精密层析结构",
      heading: "真空扩散焊接多层复合架构解析",
      paragraphs: [
        "多层烧结丝网是将多层不同规格的不锈钢编织网按严格顺序叠置，经高真空高温烧结而成的一体化多孔板。金属接触节点在固相状态下产生原子扩散结合，彻底消除了传统编织网丝线移位和焊料污染的隐患。",
        "在复合结构中，每一层丝网承担明确功能：外层保护网抵御机械磨损与颗粒冲刷，内层精滤控制网提供微米级截留精度，底层粗目骨架网则赋予复合板优异的抗压抗弯曲能力。",
        "由于各层交叉点形成了永久性冶金结合，在频繁的高压反洗、脉冲反吹及超声清洗过程中，微孔几何尺寸始终保持稳定不变。",
      ],
      keyPoints: [
        "固相扩散烧结在节点处实现原子级融合，杜绝焊料污染与钢丝移位。",
        "永久性孔隙几何结构，在周期性高压差与反复反冲洗下保持稳定截留。",
        "涵盖 3 层复合网、5 层标准结构烧结网及加厚多层冲孔复合网等规格。",
      ],
      image: {
        src: MULTI_LAYER_IMAGE,
        alt: "多层不锈钢烧结丝网微观复合结构与扩散焊接截面",
      },
    },
    specTable: {
      heading: "复合规格与典型参数对比",
      intro: "标准多层烧结配置提供 AISI 316L 与 304L 牌号，广泛应用于化工、石化、医药等工业过滤与流态化系统。",
      columns: [
        { key: "configuration", label: "结构类型", align: "left" },
        { key: "layers", label: "层数", align: "right" },
        { key: "nominalRating", label: "公称精度 (µm)", align: "right" },
        { key: "nominalThickness", label: "公称厚度 (mm)", align: "right" },
        { key: "primaryStructure", label: "层级功能组成", align: "left" },
      ],
      rows: [
        {
          configuration: "3 层标准复合网",
          layers: "3",
          nominalRating: "2 – 100",
          nominalThickness: "1.70",
          primaryStructure: "保护层 + 精度控制层 + 支撑导流层",
        },
        {
          configuration: "5 层标准烧结板",
          layers: "5",
          nominalRating: "1 – 200",
          nominalThickness: "1.70",
          primaryStructure: "保护层 + 控制层 + 分流层 + 双层加固骨架层",
        },
        {
          configuration: "多层重型定制复合板",
          layers: "2 – 9+",
          nominalRating: "1 – 200",
          nominalThickness: "0.5 – 10.0",
          primaryStructure: "多层丝网组合，可选配冲孔板作为刚性基底",
        },
      ],
      caption: "表中为典型配置公称参数，支持针对具体通量和安装尺寸进行客制化层级设计。",
    },
    features: {
      heading: "核心工程技术优势",
      intro: "专为高负荷工况设计，满足严苛的机械强度与长期孔径精度要求。",
      items: [
        {
          title: "一体化刚性板材结构",
          text: "各层丝线接触点完全冶金融合，在连续压力负荷下杜绝丝径变形与孔隙扩大问题。",
        },
        {
          title: "宽泛的过滤精度范围",
          text: "过滤精度涵盖 1 µm 至 200 µm，支持多层平纹与密纹组合，适应液体及气体精细分离。",
        },
        {
          title: "卓越的反洗再生寿命",
          text: "固定的微孔流道设计便于正向流过滤与反向脉冲吹洗，兼容化学在线清洗与超声波清洗。",
        },
        {
          title: "优异的深加工性能",
          text: "可进行激光切割、卷筒焊接、折波成型及法兰连接，加工成滤筒、滤盘与多孔滤板等元件。",
        },
      ],
    },
    related: {
      heading: "多层烧结产品结构分类",
      intro: "了解不同层级配置的技术特点，选择与您的压差、流通阻力及机械支撑要求匹配的构型。",
      cards: [
        {
          title: "3 层烧结丝网",
          text: "由保护层、微米精度控制层与支撑网组成，在高通量渗透性与可靠机械支撑之间取得平衡。",
          to: "/ThreeLayerSinteredMeshPage",
          image: {
            src: THREE_LAYER_IMAGE,
            alt: "三层烧结不锈钢丝网微观截面与结构层析",
          },
          badge: "3 层复合",
          linkLabel: "查看 3 层规格",
        },
        {
          title: "5 层标准烧结网",
          text: "工业通用基准结构，融合保护层、控制层、分流缓冲层与双层骨架，具有极高的结构稳定性。",
          to: "/LayerStandardSinteredMeshPage",
          image: {
            src: FIVE_LAYER_IMAGE,
            alt: "五层标准不锈钢烧结丝网复合板",
          },
          badge: "5 层工业标准",
          linkLabel: "查看 5 层规格",
        },
        {
          title: "多层定制复合烧结网",
          text: "真空高温扩散烧结的多层丝网或冲孔复合板，专为抵抗严重压力尖峰与高磨损固液分离设计。",
          to: "/MultiLayerPage",
          image: {
            src: MULTI_LAYER_IMAGE,
            alt: "多层不锈钢烧结丝网微观复合结构与扩散焊接截面",
          },
          badge: "定制多层复合",
          linkLabel: "查看多层复合规格",
        },
      ],
    },
    cta: {
      id: "sintered-multilayer-cta",
      eyebrow: "定制方案与图纸打样",
      heading: "获取多层烧结丝网工程选型支持",
      lead: "无论是标准 5 层烧结板还是特殊多层冲孔复合结构，我们均可根据您的工况介质、精度与尺寸要求提供整板与成品加工件。",
      secondaryLabel: "咨询技术顾问",
      secondaryTo: "/ContactPage",
      note: "支持 AISI 316L、304L 等优质不锈钢材质，按订单要求提供材料分析与质检记录。",
    },
  },
  es: {
    breadcrumbKey: "SinteredMultilayerMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Malla de alambre sinterizada",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Malla de alambre multicapa sinterizada",
      lead: "Unida por difusión en estado sólido bajo alto vacío y ciclos térmicos controlados, nuestras mallas multicapa sinterizadas combinan estabilidad de poro micrométrica, alta resistencia mecánica y excelente aptitud para retrolavado.",
      secondaryCta: {
        label: "Centro de malla sinterizada",
        to: "/SinteredWireMeshPage",
      },
      image: {
        src: MULTILAYER_HERO_IMAGE,
        alt: "Laminado de malla de alambre multicapa sinterizado al vacío",
      },
      cardNote: "Estructuras de placa multicapa",
      cardLink: {
        label: "Solicitar especificaciones",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Grado de filtración", value: "1 – 200 µm" },
        { label: "Aleaciones estándar", value: "AISI 316L / 304L" },
        { label: "Espesor de placa", value: "0.5 – 10.0 mm" },
        { label: "Proceso de unión", value: "Difusión en estado sólido" },
      ],
    },
    overview: {
      eyebrow: "Precisión estructural",
      heading: "Arquitectura laminada unida por difusión al vacío",
      paragraphs: [
        "La malla multicapa sinterizada integra varias capas de tejido de alambre en una placa porosa inseparable. Bajo alto vacío y altas temperaturas, los puntos de contacto experimentan difusión atómica sin soldadura de aporte, evitando desplazamientos de hilo y pérdidas de retención.",
        "Cada capa del compuesto cumple una función de ingeniería: la malla protectora exterior previene daños mecánicos, la capa interna de precisión determina el corte micrométrico y las mallas inferiores aportan rigidez estructural.",
        "Dado que las intersecciones están fusionadas de modo permanente, la matriz porosa conserva su geometría ante picos de presión diferencial, impulsos de retrolavado y limpieza ultrasónica periódica.",
      ],
      keyPoints: [
        "La unión por difusión en estado sólido fija todos los cruces sin soldadura ni adhesivos.",
        "Geometría de poro permanente que resiste retrolavados continuos y alta presión diferencial.",
        "Disponible en laminados de 3 capas, estándar de 5 capas y compuestos reforzados a medida.",
      ],
      image: {
        src: MULTI_LAYER_IMAGE,
        alt: "Vista macro de laminado de malla de acero inoxidable multicapa sinterizado",
      },
    },
    specTable: {
      heading: "Parámetros de especificación compuesta",
      intro: "Configuraciones multicapa estándar disponibles en grados AISI 316L y 304L para filtración y fluidificación industrial.",
      columns: [
        { key: "configuration", label: "Configuración", align: "left" },
        { key: "layers", label: "N.º capas", align: "right" },
        { key: "nominalRating", label: "Grado nominal (µm)", align: "right" },
        { key: "nominalThickness", label: "Espesor nominal (mm)", align: "right" },
        { key: "primaryStructure", label: "Composición por capas", align: "left" },
      ],
      rows: [
        {
          configuration: "Laminado estándar de 3 capas",
          layers: "3",
          nominalRating: "2 – 100",
          nominalThickness: "1.70",
          primaryStructure: "Protección + Control de precisión + Soporte de drenaje",
        },
        {
          configuration: "Placa estándar de 5 capas",
          layers: "5",
          nominalRating: "1 – 200",
          nominalThickness: "1.70",
          primaryStructure: "Protección + Control + Dispersión + 2 mallas de refuerzo",
        },
        {
          configuration: "Compuesto multicapa reforzado",
          layers: "2 – 9+",
          nominalRating: "1 – 200",
          nominalThickness: "0.5 – 10.0",
          primaryStructure: "Múltiples tejidos con opción de soporte perforado rígido",
        },
      ],
      caption: "Valores nominales de referencia. Combinaciones personalizadas disponibles según pedido.",
    },
    features: {
      heading: "Características clave de ingeniería",
      intro: "Concebida para entornos de separación exigentes que requieren certeza dimensional y resistencia mecánica continua.",
      items: [
        {
          title: "Geometría monolítica rígida",
          text: "La difusión térmica funde los puntos de contacto eliminando el movimiento de alambres bajo cargas continuas de proceso.",
        },
        {
          title: "Amplio espectro de filtración",
          text: "Disponible en rangos de 1 µm a 200 µm con combinaciones estándar o a medida para medios líquidos y gaseosos.",
        },
        {
          title: "Excelente capacidad de retrolavado",
          text: "Los conductos de poro fijos permiten impulsos inversos de limpieza y ciclos químicos sin desprendimiento de fibras.",
        },
        {
          title: "Excelente maquinabilidad",
          text: "Permite corte láser, conformado cilíndrico, plisado y soldadura para fabricar cartuchos, conos y discos de filtración.",
        },
      ],
    },
    related: {
      heading: "Variantes de construcción multicapa",
      intro: "Explore las opciones estandarizadas y a medida diseñadas para diferentes requerimientos de presión y caudal.",
      cards: [
        {
          title: "Malla sinterizada de 3 capas",
          text: "Estructurada con malla exterior protectora, capa de control micrométrica y malla de soporte rígida.",
          to: "/ThreeLayerSinteredMeshPage",
          image: {
            src: THREE_LAYER_IMAGE,
            alt: "Sección transversal de malla sinterizada de 3 capas",
          },
          badge: "3 capas",
          linkLabel: "Ver especificaciones de 3 capas",
        },
        {
          title: "Malla sinterizada estándar de 5 capas",
          text: "La configuración universal que combina protección, control, dispersión y dos capas de refuerzo estructural.",
          to: "/LayerStandardSinteredMeshPage",
          image: {
            src: FIVE_LAYER_IMAGE,
            alt: "Estructura de malla sinterizada estándar de 5 capas",
          },
          badge: "5 capas estándar",
          linkLabel: "Ver especificaciones de 5 capas",
        },
        {
          title: "Malla compuesta multicapa",
          text: "Laminados multicapa diseñados para soportar picos severos de presión diferencial y flujos abrasivos.",
          to: "/MultiLayerPage",
          image: {
            src: MULTI_LAYER_IMAGE,
            alt: "Vista macro de laminado de malla de acero inoxidable multicapa sinterizado",
          },
          badge: "Compuesto a medida",
          linkLabel: "Ver especificaciones multicapa",
        },
      ],
    },
    cta: {
      id: "sintered-multilayer-cta",
      eyebrow: "Ingeniería personalizada",
      heading: "Especifique su estructura multicapa sinterizada",
      lead: "Desde placas estándar de 5 capas hasta compuestos reforzados, suministramos láminas y componentes terminados adaptados a sus condiciones de trabajo.",
      secondaryLabel: "Contactar con ingeniería",
      secondaryTo: "/ContactPage",
      note: "Suministro en calidades AISI 316L y 304L con trazabilidad de materiales según orden.",
    },
  },
  de: {
    breadcrumbKey: "SinteredMultilayerMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Sintermetallgewebe",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Mehrlagen-Sinterdrahtgewebe",
      lead: "Durch Festkörper-Diffusionsschweißen unter Hochvakuum thermisch gefügt, vereinen unsere mehrlagigen Sintermetallgewebe mikrometergenaue Porenstabilität, extreme mechanische Steifigkeit und hervorragende Rückspülbarkeit.",
      secondaryCta: {
        label: "Übersicht Sintergewebe",
        to: "/SinteredWireMeshPage",
      },
      image: {
        src: MULTILAYER_HERO_IMAGE,
        alt: "Vakuum-diffusionsgebundenes Mehrlagen-Sinterdrahtgewebelaminat",
      },
      cardNote: "Mehrschichtige Verbundplatten",
      cardLink: {
        label: "Spezifikationen anfragen",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Filterfeinheit", value: "1 – 200 µm" },
        { label: "Standardlegierungen", value: "AISI 316L / 304L" },
        { label: "Plattenstärke", value: "0.5 – 10.0 mm" },
        { label: "Verbindungstechnik", value: "Festkörper-Diffusionsglühen" },
      ],
    },
    overview: {
      eyebrow: "Strukturelle Präzision",
      heading: "Architektur diffusionsgebundener Mehrlagenlaminate",
      paragraphs: [
        "Mehrlagen-Sintergewebe integriert mehrere Webdrahtgewebelagen zu einer untrennbaren, porösen Verbundplatte. Unter Hochvakuum und Hitze diffundieren metallische Kontaktpunkte auf atomarer Ebene ohne Lotmittel, wodurch Drahtverschiebungen ausgeschlossen werden.",
        "Jede Gewebelage erfüllt eine definierte ingenieurtechnische Funktion: äußere Schutzlagen bewahren vor Abrieb, innere Feingewebe definieren die Abscheiderate und rückseitige Stützgewebe widerstehen Druckdifferenzen.",
        "Durch die feste metallurgische Verbindung bleiben die Porenkanäle auch bei abrupten Differenzdruckspitzen, zyklischem Pulsieren und Ultraschallreinigungen formstabil.",
      ],
      keyPoints: [
        "Festkörper-Diffusionsschweißen fixiert alle Drahtkreuzungspunkte ohne Lot oder Bindemittel.",
        "Permanente Porengeometrie widersteht wiederholter Rückspülung und hohen Differenzdrücken.",
        "Erhältlich als 3-Lagen-Laminate, 5-Lagen-Standardaufbauten und maßgeschneiderte Mehrlagen-Verbunde.",
      ],
      image: {
        src: MULTI_LAYER_IMAGE,
        alt: "Makroansicht eines mehrlagigen gesinterten Edelstahldrahtgewebes",
      },
    },
    specTable: {
      heading: "Technische Verbundparameter",
      intro: "Standardmäßige Mehrlagenkonfigurationen in den Werkstoffen AISI 316L und 304L für industrielle Prozessfiltration.",
      columns: [
        { key: "configuration", label: "Konfiguration", align: "left" },
        { key: "layers", label: "Lagenanzahl", align: "right" },
        { key: "nominalRating", label: "Nennfeinheit (µm)", align: "right" },
        { key: "nominalThickness", label: "Nenndicke (mm)", align: "right" },
        { key: "primaryStructure", label: "Schichtaufbau", align: "left" },
      ],
      rows: [
        {
          configuration: "3-Lagen-Standardlaminat",
          layers: "3",
          nominalRating: "2 – 100",
          nominalThickness: "1.70",
          primaryStructure: "Schutzlage + Präzisionsfilterlage + Stützlage",
        },
        {
          configuration: "5-Lagen-Standardplatte",
          layers: "5",
          nominalRating: "1 – 200",
          nominalThickness: "1.70",
          primaryStructure: "Schutz + Filter + Verteilung + 2 Stützlagen",
        },
        {
          configuration: "Mehrlagen-Spezialverbund",
          layers: "2 – 9+",
          nominalRating: "1 – 200",
          nominalThickness: "0.5 – 10.0",
          primaryStructure: "Mehrere Gewebelagen, optional mit Lochblechverstärkung",
        },
      ],
      caption: "Referenzwerte für Standardausführungen. Kundenspezifische Lagenanordnungen auf Anfrage.",
    },
    features: {
      heading: "Wesentliche Konstruktionsvorteile",
      intro: "Konzipiert für anspruchsvolle Filtrationsanwendungen mit hohen mechanischen und strömungstechnischen Anforderungen.",
      items: [
        {
          title: "Monolithische Formstabilität",
          text: "Diffusionsverschweißte Kreuzungspunkte verhindern Drahtwanderung und Porenerweiterung bei Dauerbeanspruchung.",
        },
        {
          title: "Breiter Trennbereich",
          text: "Verfügbar von 1 µm bis 200 µm für Flüssigkeits- und Gasfiltration in Standard- oder Spezialschichtungen.",
        },
        {
          title: "Hervorragende Abreinigbarkeit",
          text: "Glatte, formstabile Strömungskanäle ermöglichen effiziente Rückspülung und chemische CIP-Reinigungszyklen.",
        },
        {
          title: "Hervorragende Weiterverarbeitbarkeit",
          text: "Zuschneidbar, rollbar, faltbar und schweißbar zu Filterkerzen, Kegelfiltern, Ronden und Flanschplatten.",
        },
      ],
    },
    related: {
      heading: "Varianten des Mehrlagenaufbaus",
      intro: "Vergleichen Sie standardisierte und maßgeschneiderte Mehrlagenkonstruktionen für Ihre spezifischen Betriebsparameter.",
      cards: [
        {
          title: "3-lagiges Sintergewebe",
          text: "Kombiniert Schutzgewebe, Kontrollschicht und Stützlage für hohe Durchlässigkeit bei zuverlässiger Stabilität.",
          to: "/ThreeLayerSinteredMeshPage",
          image: {
            src: THREE_LAYER_IMAGE,
            alt: "Querschnitt eines 3-lagigen Edelstahl-Sintergewebelaminats",
          },
          badge: "3-Lagen",
          linkLabel: "3-Lagen-Details",
        },
        {
          title: "5-lagiges Standard-Sintergewebe",
          text: "Die universelle Industriekonfiguration mit Schutz-, Filter-, Dispersions- und zwei Verstärkungslagen.",
          to: "/LayerStandardSinteredMeshPage",
          image: {
            src: FIVE_LAYER_IMAGE,
            alt: "Struktur eines 5-lagigen Standard-Sintermetallgewebes",
          },
          badge: "5-Lagen-Standard",
          linkLabel: "5-Lagen-Details",
        },
        {
          title: "Mehrlagen-Verbundsintergewebe",
          text: "Konstruiert für hohe Differenzdrücke und anspruchsvolle abrasive Fest-Flüssig-Trennungen.",
          to: "/MultiLayerPage",
          image: {
            src: MULTI_LAYER_IMAGE,
            alt: "Makroansicht eines mehrlagigen gesinterten Edelstahldrahtgewebes",
          },
          badge: "Spezialverbund",
          linkLabel: "Mehrlagen-Details",
        },
      ],
    },
    cta: {
      id: "sintered-multilayer-cta",
      eyebrow: "Individuelle Fertigung",
      heading: "Konfigurieren Sie Ihr Mehrlagen-Sintergewebe",
      lead: "Ob Standard-5-Lagen-Zuschnitte oder robuste Verbundstrukturen mit Lochblech: Wir liefern maßhaltige Filterplatten und konfektionierte Elemente.",
      secondaryLabel: "Engineering anfragen",
      secondaryTo: "/ContactPage",
      note: "Lieferbar in AISI 316L und 304L mit vollständiger Materialdokumentation nach Auftragsspezifikation.",
    },
  },
  fr: {
    breadcrumbKey: "SinteredMultilayerMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Toile métallique frittée",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Toile métallique multicouche frittée",
      lead: "Liées par diffusion à l'état solide sous vide poussé et cycles thermiques maîtrisés, nos toiles multicouches frittées allient géométrie de pore invariable, forte rigidité mécanique et excellente aptitude au contre-lavage.",
      secondaryCta: {
        label: "Gamme toile frittée",
        to: "/SinteredWireMeshPage",
      },
      image: {
        src: MULTILAYER_HERO_IMAGE,
        alt: "Stratifié de toile métallique multicouche frittée sous vide",
      },
      cardNote: "Plaques poreuses multicouches",
      cardLink: {
        label: "Demander les spécifications",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Seuil de filtration", value: "1 – 200 µm" },
        { label: "Nuances standard", value: "AISI 316L / 304L" },
        { label: "Épaisseur de plaque", value: "0.5 – 10.0 mm" },
        { label: "Procédé de liaison", value: "Diffusion à l'état solide" },
      ],
    },
    overview: {
      eyebrow: "Précision structurelle",
      heading: "Architecture des stratifiés frittés par diffusion sous vide",
      paragraphs: [
        "La toile multicouche frittée réunit plusieurs toiles métalliques tissées en une plaque poreuse monolithique. Sous vide poussé et haute température, les points de contact subissent une diffusion atomique sans brasure, évitant toute migration de fils ou modification des seuils de coupure.",
        "Chaque épaisseur du stratifié remplit une fonction technique : la couche externe protège des agressions mécaniques, la couche interne calibre la filtration micrométrique et les armatures inférieures absorbent les efforts de pression.",
        "La liaison solide permanente garantit la constance dimensionnelle des ouvertures lors des variations brutales de pression, des pulsations de rétro-lavage et des nettoyages aux ultrasons.",
      ],
      keyPoints: [
        "La liaison par diffusion à l'état solide verrouille chaque croisement sans brasure ni liant.",
        "Géométrie de pore permanente résistant aux cycles répétés de contre-lavage et aux fortes pressions.",
        "Disponible en structures 3 couches, standard 5 couches et composites multicouches renforcés sur mesure.",
      ],
      image: {
        src: MULTI_LAYER_IMAGE,
        alt: "Vue macro d'un stratifié multicouche fritté en acier inoxydable",
      },
    },
    specTable: {
      heading: "Paramètres techniques des composites",
      intro: "Configurations multicouches standards disponibles en nuances AISI 316L et 304L pour filtration liquide et gazeuse.",
      columns: [
        { key: "configuration", label: "Configuration", align: "left" },
        { key: "layers", label: "Couches", align: "right" },
        { key: "nominalRating", label: "Seuil nominal (µm)", align: "right" },
        { key: "nominalThickness", label: "Épaisseur nominale (mm)", align: "right" },
        { key: "primaryStructure", label: "Composition des couches", align: "left" },
      ],
      rows: [
        {
          configuration: "Stratifié standard 3 couches",
          layers: "3",
          nominalRating: "2 – 100",
          nominalThickness: "1.70",
          primaryStructure: "Protection + Couche filtrante de précision + Support de drainage",
        },
        {
          configuration: "Plaque standard 5 couches",
          layers: "5",
          nominalRating: "1 – 200",
          nominalThickness: "1.70",
          primaryStructure: "Protection + Contrôle + Dispersion + 2 armatures de renfort",
        },
        {
          configuration: "Composite multicouche renforcé",
          layers: "2 – 9+",
          nominalRating: "1 – 200",
          nominalThickness: "0.5 – 10.0",
          primaryStructure: "Empilement de toiles multiples avec tôle perforée optionnelle",
        },
      ],
      caption: "Données de référence standards. Combinaisons et dimensions sur mesure sur consultation.",
    },
    features: {
      heading: "Atouts techniques essentiels",
      intro: "Conçu pour les procédés exigeants nécessitant une constance de filtration et une robustesse prolongée.",
      items: [
        {
          title: "Rigidité monolithique",
          text: "Les croisements soudés par diffusion empêchent tout déplacement de fil et toute déformation sous charge continue.",
        },
        {
          title: "Large spectre de coupure",
          text: "Disponible de 1 µm à 200 µm pour répondre aux exigences de filtration liquide et gazeuse.",
        },
        {
          title: "Régénération par contre-lavage",
          text: "Canaux réguliers permettant des inversions de flux et des nettoyages chimiques sans altération de la toile.",
        },
        {
          title: "Usinabilité et formabilité",
          text: "Se découpe, se roule, se plisse et se soude pour confectionner cartouches, cônes et disques de filtration.",
        },
      ],
    },
    related: {
      heading: "Variantes de structures multicouches",
      intro: "Découvrez nos configurations standards et personnalisées adaptées à vos pertes de charge et débits de procédé.",
      cards: [
        {
          title: "Toile frittée 3 couches",
          text: "Composée d'une toile protectrice, d'une couche filtrante micrométrique et d'un support rigide.",
          to: "/ThreeLayerSinteredMeshPage",
          image: {
            src: THREE_LAYER_IMAGE,
            alt: "Coupe transversale d'un stratifié fritté à 3 couches en acier inoxydable",
          },
          badge: "3 couches",
          linkLabel: "Détails 3 couches",
        },
        {
          title: "Toile frittée standard 5 couches",
          text: "La configuration universelle associant protection, contrôle, dispersion et deux armatures de renfort.",
          to: "/LayerStandardSinteredMeshPage",
          image: {
            src: FIVE_LAYER_IMAGE,
            alt: "Structure de toile métallique frittée 5 couches standard",
          },
          badge: "5 couches standard",
          linkLabel: "Détails 5 couches",
        },
        {
          title: "Toile composite multicouche",
          text: "Toile frittée renforcée pour résister aux pics de pression différentielle et aux fluides abrasifs.",
          to: "/MultiLayerPage",
          image: {
            src: MULTI_LAYER_IMAGE,
            alt: "Vue macro d'un stratifié multicouche fritté en acier inoxydable",
          },
          badge: "Composite sur mesure",
          linkLabel: "Détails multicouche",
        },
      ],
    },
    cta: {
      id: "sintered-multilayer-cta",
      eyebrow: "Fabrication sur mesure",
      heading: "Définissez votre structure multicouche frittée",
      lead: "Des plaques 5 couches standards aux composites lourds avec tôle perforée, nous fabriquons des composants adaptés à vos installations.",
      secondaryLabel: "Consulter l'ingénierie",
      secondaryTo: "/ContactPage",
      note: "Nuances AISI 316L et 304L avec certificats matière selon les spécifications de votre commande.",
    },
  },
  pt: {
    breadcrumbKey: "SinteredMultilayerMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Malha de arame sinterizada",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Malha de arame multicamadas sinterizada",
      lead: "Unidas por difusão em estado sólido sob alto vácuo e ciclos térmicos controlados, nossas malhas multicamadas sinterizadas unem estabilidade dimensional de poros, grande rigidez estrutural e facilidade de retrolavagem.",
      secondaryCta: {
        label: "Visão geral de telas sinterizadas",
        to: "/SinteredWireMeshPage",
      },
      image: {
        src: MULTILAYER_HERO_IMAGE,
        alt: "Laminado de malha de arame multicamadas sinterizado a vácuo",
      },
      cardNote: "Placas porosas multicamadas",
      cardLink: {
        label: "Solicitar dados técnicos",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Grau de filtração", value: "1 – 200 µm" },
        { label: "Ligas padrão", value: "AISI 316L / 304L" },
        { label: "Espessura de chapa", value: "0.5 – 10.0 mm" },
        { label: "Processo de união", value: "Difusão em estado sólido" },
      ],
    },
    overview: {
      eyebrow: "Precisão estrutural",
      heading: "Arquitetura laminada unida por difusão a vácuo",
      paragraphs: [
        "A malha multicamadas sinterizada combina múltiplas telas de arame em uma placa porosa indissociável. Sob alto vácuo e altas temperaturas, os nós de contato sofrem difusão atômica sem adição de solda, evitando a migração de fios e alteração de retenção.",
        "Cada camada cumpre um papel projetado: a tela externa protege contra atrito mecânico, a malha interna de precisão assegura a retenção micrométrica e as telas de suporte inferiores absorvem esforços de pressão.",
        "Com interseções permanentemente fundidas, os poros mantêm dimensões estáveis durante pulsos de retrolavagem e higienização ultrassônica contínua.",
      ],
      keyPoints: [
        "A sinterização por difusão atômica funde os cruzamentos sem solda ou adesivos.",
        "Geometria permanente de poros resistente a ciclos repetitivos de retrolavagem e alta pressão.",
        "Disponível em laminados de 3 camadas, padrão de 5 camadas e compostos reforçados sob medida.",
      ],
      image: {
        src: MULTI_LAYER_IMAGE,
        alt: "Visão macro de laminado multicamadas sinterizado de aço inoxidável",
      },
    },
    specTable: {
      heading: "Parâmetros técnicos dos compostos",
      intro: "Configurações multicamadas padrão disponíveis em ligas AISI 316L e 304L para filtração industrial e fluidização.",
      columns: [
        { key: "configuration", label: "Configuração", align: "left" },
        { key: "layers", label: "N.º camadas", align: "right" },
        { key: "nominalRating", label: "Retenção nominal (µm)", align: "right" },
        { key: "nominalThickness", label: "Espessura nominal (mm)", align: "right" },
        { key: "primaryStructure", label: "Composição por camadas", align: "left" },
      ],
      rows: [
        {
          configuration: "Laminado padrão de 3 camadas",
          layers: "3",
          nominalRating: "2 – 100",
          nominalThickness: "1.70",
          primaryStructure: "Proteção + Malha de controle + Suporte de drenagem",
        },
        {
          configuration: "Placa padrão de 5 camadas",
          layers: "5",
          nominalRating: "1 – 200",
          nominalThickness: "1.70",
          primaryStructure: "Proteção + Controle + Dispersão + 2 telas de reforço",
        },
        {
          configuration: "Composto multicamadas reforçado",
          layers: "2 – 9+",
          nominalRating: "1 – 200",
          nominalThickness: "0.5 – 10.0",
          primaryStructure: "Múltiplas camadas de malha com reforço perfurado opcional",
        },
      ],
      caption: "Valores nominais para referência. Composições e formatos sob encomenda mediante projeto.",
    },
    features: {
      heading: "Principais diferenciais técnicos",
      intro: "Desenvolvida para operações de separação que demandam consistência de retenção e durabilidade contínua.",
      items: [
        {
          title: "Rigidez monolítica",
          text: "Os pontos de fusão por difusão eliminam o deslocamento de fios sob variações contínuas de vazão.",
        },
        {
          title: "Faixa ampla de filtragem",
          text: "Opções de 1 µm a 200 µm em construções padronizadas ou customizadas para líquidos e gases.",
        },
        {
          title: "Alta eficiência de retrolavagem",
          text: "Canais rígidos e lisos viabilizam retrolavagem rápida e ciclos de higienização química CIP.",
        },
        {
          title: "Ampla conformabilidade",
          text: "Pode ser cortada a laser, calandrada, pregueada e soldada na forma de tubos, discos e placas flangeadas.",
        },
      ],
    },
    related: {
      heading: "Modelos de construção multicamadas",
      intro: "Conheça as estruturas padronizadas e reforçadas projetadas para diferentes exigências de vazão e resistência mecânica.",
      cards: [
        {
          title: "Malha sinterizada de 3 camadas",
          text: "Desenvolvida com malha externa de proteção, camada de controle micrométrico e tela de suporte estrutural.",
          to: "/ThreeLayerSinteredMeshPage",
          image: {
            src: THREE_LAYER_IMAGE,
            alt: "Corte transversal de malha sinterizada de 3 camadas em aço inoxidável",
          },
          badge: "3 camadas",
          linkLabel: "Ver detalhes de 3 camadas",
        },
        {
          title: "Malha sinterizada padrão de 5 camadas",
          text: "A configuração industrial universal que combina proteção, controle, dispersão e dois reforços estruturais.",
          to: "/LayerStandardSinteredMeshPage",
          image: {
            src: FIVE_LAYER_IMAGE,
            alt: "Estrutura de malha sinterizada padrão de 5 camadas",
          },
          badge: "5 camadas padrão",
          linkLabel: "Ver detalhes de 5 camadas",
        },
        {
          title: "Malha composta multicamadas",
          text: "Telas multicamadas reforçadas para suportar picos severos de pressão diferencial e meios abrasivos.",
          to: "/MultiLayerPage",
          image: {
            src: MULTI_LAYER_IMAGE,
            alt: "Visão macro de laminado multicamadas sinterizado de aço inoxidável",
          },
          badge: "Composto sob medida",
          linkLabel: "Ver detalhes multicamadas",
        },
      ],
    },
    cta: {
      id: "sintered-multilayer-cta",
      eyebrow: "Engenharia sob medida",
      heading: "Especifique seu laminado sinterizado multicamadas",
      lead: "De placas padrão de 5 camadas a conjuntos com chapas perfuradas, fornecemos peças brutas ou usinadas conforme sua aplicação.",
      secondaryLabel: "Fale com a engenharia",
      secondaryTo: "/ContactPage",
      note: "Disponível em aços inoxidáveis AISI 316L e 304L com rastreabilidade especificada em pedido.",
    },
  },
};
